// /tours/new — create a tour route.
//
// Per-leg city autocomplete + optional per-leg dates + corridor radius slider.
// Validation rides the shared zod schema so server + client agree on the rules.
// CSRF + credentials handled via the standard helpers.

import {
  Anchor,
  Button,
  Group,
  Paper,
  Slider,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  createTourRouteInputSchema,
  PLANNING_LIMITS,
} from '@wwaave/shared/zod';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  CityLegList,
  type CityLegValue,
} from '../../components/tours/CityLegList';
import { PageHero } from '../../components/ui';
import { ApiError, useApiForm } from '../../hooks/useApiForm';
import { showToast } from '../../lib/toast';

export const Route = createFileRoute('/tours/new')({
  component: ToursNewPage,
});

type FormValues = {
  name: string;
  cities: CityLegValue[];
  from_date: string;
  to_date: string;
  notify_on_match: boolean;
  corridor_radius_km: number;
};

const INITIAL_VALUES: FormValues = {
  name: '',
  cities: [{ name: '' }],
  from_date: '',
  to_date: '',
  notify_on_match: true,
  corridor_radius_km: PLANNING_LIMITS.corridorRadiusDefaultKm,
};

const RADIUS_MARKS = [
  { value: 0, label: 'Exact city only' },
  { value: 50, label: '50 km' },
  { value: PLANNING_LIMITS.corridorRadiusMaxKm, label: 'Wide net' },
];

function ToursNewPage(): React.JSX.Element {
  const navigate = useNavigate();

  const {
    form,
    submit,
    submitting,
    serverError: submitError,
  } = useApiForm<FormValues>({
    endpoint: '/api/me/tour-routes',
    schema: createTourRouteInputSchema,
    initialValues: INITIAL_VALUES,
    transform: (values) => ({
      name: values.name.trim(),
      cities: values.cities
        .filter((leg) => leg.name.trim().length > 0)
        .map((leg) => ({
          name: leg.name.trim(),
          ...(leg.from_date !== undefined && leg.from_date !== ''
            ? { from_date: leg.from_date }
            : {}),
          ...(leg.to_date !== undefined && leg.to_date !== ''
            ? { to_date: leg.to_date }
            : {}),
        })),
      from_date: values.from_date,
      to_date: values.to_date,
      notify_on_match: values.notify_on_match,
      corridor_radius_km: values.corridor_radius_km,
    }),
    onSuccess: () => {
      showToast({ kind: 'success', message: 'Route saved.' });
      navigate({ to: '/tours' });
    },
    mapError: (err) =>
      err instanceof ApiError
        ? err.status === 409
          ? 'This route changed in another tab. Try again.'
          : err.message
        : "We couldn't save that route.",
  });

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <PageHero
          eyebrow="Tour routes"
          title="Where are you headed?"
          lede="Pick the cities and the rough window. We'll send a note when a fit shows up."
          action={
            <Anchor component={Link} to="/tours" size="sm">
              Back to routes
            </Anchor>
          }
        />

        <Paper withBorder p="lg" radius="md">
          <form onSubmit={submit}>
            <Stack gap="md">
              <TextInput
                label="Route name"
                placeholder="East coast, late winter"
                description="Just for you. For example, 'East coast, late winter'."
                maxLength={PLANNING_LIMITS.tourRouteNameMax}
                required
                {...form.getInputProps('name')}
              />

              <Stack gap={4}>
                <Text fw={500} size="sm">
                  Cities, in order
                </Text>
                <Text size="xs" c="dimmed">
                  Add {PLANNING_LIMITS.tourRouteMinCities}–
                  {PLANNING_LIMITS.tourRouteMaxCities}. Use the arrows to reorder.
                </Text>
                <CityLegList
                  value={form.values.cities}
                  onChange={(next) => form.setFieldValue('cities', next)}
                  routeFrom={form.values.from_date}
                  routeTo={form.values.to_date}
                  error={form.errors.cities}
                />
              </Stack>

              <Group grow>
                <TextInput
                  type="date"
                  label="From"
                  required
                  {...form.getInputProps('from_date')}
                />
                <TextInput
                  type="date"
                  label="To"
                  required
                  {...form.getInputProps('to_date')}
                />
              </Group>

              <Stack gap={4}>
                <Text fw={500} size="sm">
                  Corridor radius: {form.values.corridor_radius_km} km
                </Text>
                <Text size="xs" c="dimmed">
                  We'll surface venues this far from each city on your route.
                </Text>
                <Slider
                  min={PLANNING_LIMITS.corridorRadiusMinKm}
                  max={PLANNING_LIMITS.corridorRadiusMaxKm}
                  step={5}
                  marks={RADIUS_MARKS}
                  value={form.values.corridor_radius_km}
                  onChange={(v) => form.setFieldValue('corridor_radius_km', v)}
                  mb="xl"
                />
              </Stack>

              <Switch
                label="Send me a notification when something matches"
                description="You can change this any time."
                {...form.getInputProps('notify_on_match', { type: 'checkbox' })}
              />

              {submitError !== null ? (
                <Text c="red" role="alert">
                  {submitError}
                </Text>
              ) : null}

              <Group justify="flex-end" gap="sm">
                <Button
                  type="button"
                  variant="subtle"
                  onClick={() => navigate({ to: '/tours' })}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={submitting}
                  variant="gradient"
                  gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                >
                  Save route
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </AppShell>
  );
}
